import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useUserLocale } from 'src/user/UserContext'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { GridColDef, useGridApiRef } from '@mui/x-data-grid-pro'
import { useClient } from 'urql'
import { S } from 'src/shared/styled/S'
import { Feature_Config, Jsonb_Comparison_Exp, Project_Base_Bool_Exp, Query_Root } from 'src/@types/graphql'
import { PROJECT } from 'src/shared/constants/constants'
import { Utils } from 'src/shared/utils/Utils'
import { fetchProjectYearlyReportsKpi } from 'src/screens/shared/project/details/project-features/projectFeaturesYearlyKpiQueries'
import { DataGridContainer } from 'src/shared/grid/DataGridContainer'
import { useGridTranslateHook } from 'src/shared/utils/hooks/grid-translate-hook'
import { muiTheme } from 'src/theme/theme'
import { Tooltip } from '@mui/material'
import { useError } from 'src/shared/utils/hooks/page-loading-error-hook'
import { sortBy } from 'lodash'

interface Props {
  projectId: number
  baseUrl: '/pf-kap' | '/pf-pgv'
}

interface ProjectFeatureKpiYearlyAnnualReportRowData {
  id: number
  featureType: string
  feature: string
  featureTooltip: string | null
  otherDescription: string | null

  // yearly annual reports
  [year: string]: string | number | null

  total: number
}

const mapFeatureTypeKpiToProjectKpiYearlyAnnualReportRowData = (
  featureKpis: Feature_Config[],
): ProjectFeatureKpiYearlyAnnualReportRowData[] => {
  const sortedFeatureKpis = sortBy(featureKpis, [
    'feature_type_config.feature_type_sorting_configs[0].sort_order',
    'feature_group_config.feature_group_sorting_configs[0].sort_order',
    'feature_sorting_configs[0].sort_order',
  ])
  const featureKpiMap: Map<string, ProjectFeatureKpiYearlyAnnualReportRowData> = new Map()

  sortedFeatureKpis.forEach((featureKpi, index) => {
    const initialRowData: ProjectFeatureKpiYearlyAnnualReportRowData = {
      id: index,
      feature: featureKpi.names,
      featureType: featureKpi.feature_type_config.names,
      featureTooltip: featureKpi.tooltips,
      otherDescription: featureKpi.feature_values?.[0]?.other_description ?? null,
      total: 0,
    }

    featureKpiMap.set(featureKpi.names, initialRowData)
  })

  sortedFeatureKpis
    .flatMap((i) => i.feature_kpi_annual_reports)
    .forEach((featureAnnualReport) => {
      const row = featureKpiMap.get(featureAnnualReport.feature_config.names)!

      const yearAnnualReport = featureAnnualReport.milestone.year_in_focus

      row[yearAnnualReport] = featureAnnualReport.annual_value ?? null

      row.total += featureAnnualReport.annual_value ?? 0
    })

  return Array.from(featureKpiMap.values())
}

const getYearsForKpi = (featureKpis: Feature_Config[]): number[] => {
  if (featureKpis.length === 0) {
    return []
  }

  const milestoneAllYears = featureKpis
    .flatMap((i) => i.feature_kpi_annual_reports)
    .map((i) => i.milestone.year_in_focus)

  const uniqueYears = new Set(milestoneAllYears)

  return Array.from(uniqueYears).sort() as unknown as number[]
}

export const ProjectFeaturesYearlyKpiGrid = ({ projectId, baseUrl }: Props): ReactElement => {
  const locale = useUserLocale()
  const { getMessage } = useMessageSource()
  const gridTranslations = useGridTranslateHook()
  const apiRef = useGridApiRef()
  const urqlClient = useClient()
  const setError = useError()

  const [featureKpi, setFeatureKpi] = useState<Feature_Config[]>([])

  const projectType = Utils.resolveProcess(baseUrl)
  const rowsEmpty = featureKpi.flatMap((i) => i.feature_kpi_annual_reports).length === 0

  const fetchData = useCallback(async () => {
    const pfKapQuery = {
      pf_kap_projects: {
        id: {
          _eq: projectId,
        },
      },
    }

    const pfPgvQuery = {
      pf_pgv_projects: {
        id: {
          _eq: projectId,
        },
      },
    }

    const projectBaseQuery: Project_Base_Bool_Exp = {
      ...(projectType === PROJECT.PF_KAP && pfKapQuery),
      ...(projectType === PROJECT.PF_PGV && pfPgvQuery),
    }

    const featureConfigKPIWhere: Jsonb_Comparison_Exp = {
      _contains: [projectType],
    }

    const featureConfigSortFilter = {
      process: {
        _eq: projectType,
      },
    }

    const { data } = await urqlClient
      .query<{
        feature_config: Query_Root['feature_config']
      }>(fetchProjectYearlyReportsKpi, {
        projectBaseWhere: projectBaseQuery,
        featureConfigKPIWhere: featureConfigKPIWhere,
        language: locale,
        featureTypeSortingConfig: featureConfigSortFilter,
        featureGroupSortingConfig: featureConfigSortFilter,
        featureSortingConfig: featureConfigSortFilter,
      })
      .toPromise()

    if (data?.feature_config) {
      setFeatureKpi(data.feature_config)
    } else {
      setError()
    }
  }, [urqlClient, projectId, locale])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const columns = useMemo(() => {
    const columns: GridColDef[] = []

    if (!rowsEmpty) {
      columns.push({
        field: 'featureType',
        headerName: getMessage('label.feature.type'),
        sortable: false,
        type: 'string',
        flex: 0.6,
        minWidth: 200,
        renderCell({ value }) {
          return (
            <S.DataGrid.Typography variant="subtitle2" sx={{ color: muiTheme.palette.text.disabled }}>
              {value}
            </S.DataGrid.Typography>
          )
        },
      })

      columns.push({
        field: 'feature',
        headerName: getMessage('label.feature'),
        sortable: false,
        type: 'string',
        flex: 1,
        minWidth: 200,
        renderCell: (params) => {
          let featureName = ''
          const value = params.value
          if (value) {
            // for "Other" features display also the corresponding description
            featureName = params.row.otherDescription ? `${value}: ${params.row.otherDescription}` : value
          }
          return (
            <S.DataGrid.CenterDataGridCell>
              <S.Span.TextOverflow $withTooltip>{value && featureName}</S.Span.TextOverflow>
              {params.row.featureTooltip && (
                <Tooltip placement="right" title={params.row.featureTooltip}>
                  <S.Icons.GridTooltipInfo />
                </Tooltip>
              )}
            </S.DataGrid.CenterDataGridCell>
          )
        },
      })

      // column for years
      getYearsForKpi(featureKpi).map((y) => {
        columns.push({
          field: `${y}`,
          headerName: `${y}`,
          sortable: false,
          flex: 0.3,
          minWidth: 100,
          type: 'number',
          renderCell: ({ value }) => {
            if (value == null || value === '' || Number.isNaN(value)) {
              return '-'
            }
            return <S.Span.TextOverflow>{value?.toLocaleString('de-CH')}</S.Span.TextOverflow>
          },
        })
      })

      columns.push({
        field: 'total',
        headerName: getMessage('label.actuals.to.date'),
        sortable: false,
        type: 'number',
        flex: 0.3,
        minWidth: 100,
        renderCell: ({ value }) => {
          if (value == null || value === '' || Number.isNaN(value)) {
            return '-'
          }
          return <S.Span.TextOverflow>{value?.toLocaleString('de-CH')}</S.Span.TextOverflow>
        },
      })
    }

    return columns
  }, [featureKpi, rowsEmpty, getMessage])

  const rowsYearly = useMemo(() => {
    if (!rowsEmpty) {
      return mapFeatureTypeKpiToProjectKpiYearlyAnnualReportRowData(featureKpi)
    }
    return []
  }, [featureKpi, rowsEmpty])

  return (
    <DataGridContainer>
      <S.DataGrid.ViewEditMode
        $viewMode
        rows={rowsYearly}
        columns={columns}
        hideFooter
        disableColumnReorder
        disableRowSelectionOnClick
        disableColumnMenu
        disableColumnSelector
        disableColumnFilter
        disableMultipleColumnsSorting
        apiRef={apiRef as any}
        onCellEditStop={() => {}}
        localeText={gridTranslations}
        columnHeaderHeight={rowsEmpty ? 0 : undefined}
      />
    </DataGridContainer>
  )
}
